"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const cardsData = [{
  step: "01",
  title: "Identify the Existing Controller",
  description: "Record the model, label and visible connections."
}, {
  step: "02",
  title: "Compare Connections",
  description: "Connector style and wiring arrangement can matter as much as external dimensions."
}, {
  step: "03",
  title: "Confirm Before Replacing",
  description: "Do not substitute a controller simply because the enclosure or controls look similar."
}];
export default function Controllers() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="mechanicalbullcontrollersguide" data-nav="light" className="w-full border-t border-border bg-background py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="flex flex-col">
          {/* Section Header & Narrative */}
          <motion.div variants={itemVariants} className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-serif font-bold uppercase tracking-wider text-foreground">
              Mechanical Bull Controllers &amp; Control Boxes
            </h2>
            <div className="mt-6 space-y-4 text-base md:text-lg leading-relaxed text-muted-foreground">
              <p>
                The controller is one of the most important components to
                identify correctly. It manages how the operator controls the
                machine, and different systems can use different interfaces,
                connectors and control architecture.
              </p>
              <p>
                If you are looking for a mechanical bull controller or
                replacement control box, start by identifying the controller
                already installed on your machine. Photograph the front controls,
                rear connections, labels and any model or part numbers that are
                still visible.
              </p>
              <p>
                Do not assume a newer-looking controller is automatically a
                replacement for an older one. Compatibility should be confirmed
                before the existing control system is changed.
              </p>
            </div>
          </motion.div>

          {/* Cards Grid */}
          <motion.div variants={containerVariants} className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {cardsData.map((card, i) => <motion.div key={card.step} data-index={i} variants={itemVariants} className="group border border-border bg-card p-6 rounded-none transition-[border-color,box-shadow] duration-200 hover:border-primary shadow-xs">
                <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
                  <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                    Step {card.step}
                  </span>
                  <div className="h-1.5 w-1.5 bg-primary rounded-none" />
                </div>
                <h3 className="text-xl font-serif font-bold uppercase tracking-wide text-card-foreground">
                  {card.title}
                </h3>
                <p className="mt-3 text-sm leading-normal text-muted-foreground">
                  {card.description}
                </p>
              </motion.div>)}
          </motion.div>

          {/* Centered CTA Area */}
          <motion.div variants={itemVariants} className="mt-12 flex justify-center pt-4">
            <Button asChild className="rounded-none bg-primary text-primary-foreground font-serif uppercase tracking-wider px-8 py-6 text-base font-semibold hover:bg-primary/90 transition-colors shadow-xs">
              <Link to="#">
                VIEW AVAILABLE CONTROLLERS
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}